import { getRequest } from 'Services/baseRequests/getRequest'
import {
  GetEmployerAdminInvoicesApiResponse,
  GetEmployerAdminInvoicesResponse,
  RequestResponse
} from 'Services/apiResponseTypes'
import {
  GetDetailedInvoiceResponse,
  GetInvoiceDetailsApiResponse
} from 'Services/JOONAdmin/adminApiResponseTypes'
import {
  parseDetailedInvoiceApiResponseData,
  parseEmployerAdminInvoiceApiResponseData
} from 'Services/invoices/parsers'
import { postRequest, postRequestDownloadFile } from 'Services/baseRequests/postRequest'

import { EmployerInfo } from 'Utils/types/employerInfo'
import { Invoice, InvoiceState } from 'Utils/types/invoice'
import { PaymentMethod } from 'Utils/types/paymentMethod'
import { CountryCode } from 'Utils/types/countries'

export type GetEmployerAdminInvoicePayload = {
  perPage: number
  pageNumber: number
  employerId: EmployerInfo['id']
  state?: InvoiceState | ''
  countryCode?: CountryCode | ''
  sortBy?: string
  sortDirection?: 'asc' | 'desc'
}

const mapSortFieldToDatabase = (frontendField: string): string => {
  const fieldMapping: Record<string, string> = {
    date: 'date_created',
    countryCode: 'country_code',
    state: 'state',
    benefitTransferFees: 'benefit_transfer_fees'
  }
  return fieldMapping[frontendField] || frontendField
}

export const getEmployerAdminInvoices = async ({
  employerId,
  pageNumber,
  perPage,
  state,
  countryCode,
  sortBy,
  sortDirection
}: GetEmployerAdminInvoicePayload): Promise<GetEmployerAdminInvoicesResponse> => {
  const params = new URLSearchParams({
    page: pageNumber.toString(),
    per_page: perPage.toString()
  })

  if (state) params.append('state', state)
  if (countryCode) params.append('country_code', countryCode)
  if (sortBy) params.append('sort_by', mapSortFieldToDatabase(sortBy))
  if (sortDirection) params.append('sort_direction', sortDirection)

  const response = await getRequest<GetEmployerAdminInvoicesApiResponse>(
    `/employers/${employerId}/invoices/grouped?${params.toString()}`
  )

  return {
    statusCode: response.statusCode,
    success: response?.success ?? false,
    message: response?.message ?? '',
    data: {
      invoices: response?.data?.invoices.map(parseEmployerAdminInvoiceApiResponseData) ?? [],
      totalInvoices: response?.data?.total_records ?? 0
    }
  }
}

export const getEmployerAdminInvoiceDetails = async (
  invoiceId: number,
  employerId: number
): Promise<GetDetailedInvoiceResponse> => {
  const response = await getRequest<GetInvoiceDetailsApiResponse>(
    `/employers/${employerId}/invoices/${invoiceId}/fetch_invoice_details`
  )

  return {
    success: response?.success ?? false,
    message: response?.message ?? '',
    data: response?.data
      ? { invoice: parseDetailedInvoiceApiResponseData(response.data.invoice) }
      : undefined
  }
}

export const downloadInvoice = async (employerId: EmployerInfo['id'], invoiceId: Invoice['id']) => {
  const response = await postRequestDownloadFile<ArrayBuffer>(
    `/employers/${employerId}/invoices/${invoiceId}/generate_csv`,
    undefined,
    { responseType: 'arraybuffer' }
  )

  return response
    ? {
        success: true,
        statusCode: response.statusCode ?? undefined,
        message: '',
        data: response.data
      }
    : {
        success: false,
        message: '',
        data: undefined
      }
}

export type SubmitInvoicePaymentPayload = {
  employerId: EmployerInfo['id']
  invoiceId: Invoice['id']
  paymentMethodId: PaymentMethod['id']
  idempotencyKey: string
}
export const submitInvoicePayment = async ({
  employerId,
  invoiceId,
  paymentMethodId,
  idempotencyKey
}: SubmitInvoicePaymentPayload): Promise<RequestResponse> => {
  const response = await postRequest<
    RequestResponse,
    { payment_method_id: PaymentMethod['id']; idempotency_key: string }
  >(`/employers/${employerId}/invoices/${invoiceId}/submit_payment`, {
    payment_method_id: paymentMethodId,
    idempotency_key: idempotencyKey
  })

  return {
    statusCode: response.statusCode,
    success: response?.success ?? false,
    message: response?.message ?? '',
    data: response?.data
  }
}

export type GetStripeInvoiceUrlPayload = {
  employerId: number
  invoiceId: number
}

type GetStripeInvoicePaymentUrlResponse = RequestResponse<GetStripeInvoicePaymentUrlResponseData>
type GetStripeInvoicePaymentUrlResponseData = {
  url: string
}

export const getStripeInvoiceUrl = async ({
  employerId,
  invoiceId
}: GetStripeInvoiceUrlPayload): Promise<GetStripeInvoicePaymentUrlResponse> => {
  const response = await getRequest<GetStripeInvoicePaymentUrlResponse>(
    `/employers/${employerId}/invoices/${invoiceId}/stripe_invoice_link`
  )

  return {
    statusCode: response.statusCode,
    success: response?.success ?? false,
    message: response?.message ?? '',
    data: { url: response?.data?.url || '' }
  }
}
